import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Layers, ChevronLeft, ChevronRight, Package, ExternalLink, Loader2 } from 'lucide-react';
import axios from 'axios';
import { API } from '@/api';
import { useLang } from '../contexts/LanguageContext';
import { pickLocalizedText } from '../utils/localizedContent';


export default function BoosterDetail() {
  const { boosterId } = useParams();
  const { lang } = useLang();
  const [booster, setBooster] = useState(null);
  const [cards, setCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cardsLoading, setCardsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalCards, setTotalCards] = useState(0);
  const pageSize = 20;
  const text = (es, en) => pickLocalizedText(lang, es, en);

  const fetchBooster = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/boosters/${boosterId}`);
      setBooster(data);
    } catch (err) {
      console.error('Failed to fetch booster:', err);
    }
    setLoading(false);
  }, [boosterId]);

  const fetchCards = useCallback(async () => {
    if (!booster?.set_id || !booster?.tcg_type) return;
    setCardsLoading(true);
    try {
      const { data } = await axios.get(`${API}/tcg/sets/${booster.set_id}/cards`, {
        params: { tcg_type: booster.tcg_type, page, page_size: pageSize }
      });
      setCards(data.cards || []);
      setTotalCards(data.total || 0);
    } catch (err) {
      console.error('Failed to fetch cards:', err);
    }
    setCardsLoading(false);
  }, [booster, page]);

  useEffect(() => {
    fetchBooster();
  }, [fetchBooster]);

  useEffect(() => {
    if (booster) {
      fetchCards();
    }
  }, [booster, fetchCards]);

  const totalPages = Math.ceil(totalCards / pageSize);

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center justify-center min-h-[50vh]">
          <Loader2 size={32} className="animate-spin text-[#C9A962]" />
        </div>
      </div>
    );
  }

  if (!booster) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="text-center py-20">
          <Layers size={48} className="mx-auto mb-4 text-[#A8B5AE] opacity-30" />
          <p className="text-[#A8B5AE]">{text('Booster no encontrado', 'Booster not found')}</p>
          <Link to="/store" className="text-[#C9A962] hover:underline mt-4 inline-block">
            {text('← Volver a la tienda', '← Back to store')}
          </Link>
        </div>
      </div>
    );
  }

  const tcgLabels = {
    pokemon: 'Pokémon TCG',
    magic: 'Magic: The Gathering',
    yugioh: 'Yu-Gi-Oh!',
    onepiece: 'One Piece TCG'
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-8 page-enter">
      {/* Header */}
      <div className="flex items-start gap-6 mb-8">
        {booster.logo_url && (
          <img src={booster.logo_url} alt={booster.name} className="w-24 h-24 object-contain" />
        )}
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <span className="bg-[#0D5E4A]/30 text-[#2D8B70] text-xs px-2 py-0.5 rounded-full font-semibold">
              {tcgLabels[booster.tcg_type] || booster.tcg_type}
            </span>
            <span className="text-[#A8B5AE] text-xs">{booster.set_code}</span>
          </div>
          <h1 className="text-3xl font-black text-[#F5F0E6] mb-2" style={{ fontFamily: 'Cinzel, serif' }}>
            {booster.name}
          </h1>
          <div className="flex items-center gap-4 text-sm text-[#A8B5AE]">
            {booster.release_date && <span>{text('Lanzamiento:', 'Released:')} {booster.release_date}</span>}
            <span>{totalCards} {text('cartas', 'cards')}</span>
          </div>
        </div>
      </div>

      {/* Linked Products */}
      {booster.linked_products?.length > 0 && (
        <div className="mb-8">
          <h2 className="text-[#F5F0E6] font-bold text-lg mb-4 flex items-center gap-2">
            <Package size={18} className="text-[#C9A962]" />
            {text('Productos disponibles', 'Available Products')}
          </h2>
          <div className="flex gap-4 overflow-x-auto pb-2">
            {booster.linked_products.map(product => (
              <Link
                key={product.product_id}
                to={`/store/${product.product_id}`}
                className="flex-shrink-0 bg-[#0F1F1A] border border-[#C9A962]/20 rounded-xl p-3 hover:border-[#C9A962]/50 transition-colors min-w-[200px]"
                data-testid={`linked-product-${product.product_id}`}
              >
                <div className="flex items-center gap-3">
                  <img
                    src={product.images?.[0] || 'https://via.placeholder.com/60'}
                    alt={product.name}
                    className="w-12 h-12 rounded-lg object-cover"
                  />
                  <div className="flex-1 min-w-0">
                    <p className="text-[#F5F0E6] font-semibold text-sm truncate">{product.name}</p>
                    <p className="text-[#C9A962] font-bold">{product.price?.toFixed(2)}€</p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Cards Grid */}
      <div className="mb-6">
        <h2 className="text-[#F5F0E6] font-bold text-lg mb-4">
          {text('Cartas en este set', 'Cards in this set')}
        </h2>
        
        {cardsLoading ? (
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8 gap-4">
            {[...Array(pageSize)].map((_, i) => (
              <div key={`card-skeleton-${i}`} className="aspect-[2.5/3.5] skeleton rounded-lg"></div>
            ))}
          </div>
        ) : cards.length === 0 ? (
          <div className="text-center py-12 text-[#A8B5AE]">
            <p>{text('No se pudieron cargar las cartas', 'Could not load cards')}</p>
          </div>
        ) : (
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8 gap-4">
            {cards.map((card, idx) => (
              <div
                key={card.external_id || idx}
                className="group relative"
                data-testid={`booster-card-${card.external_id || idx}`}
              >
                <div className="aspect-[2.5/3.5] rounded-lg overflow-hidden bg-[#0F1F1A] border border-[#C9A962]/10 hover:border-[#C9A962]/40 transition-all hover:shadow-lg hover:shadow-[#C9A962]/10">
                  {card.image_url ? (
                    <img
                      src={card.image_url}
                      alt={card.name}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      loading="lazy"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[#A8B5AE] text-xs text-center p-2">
                      {card.name}
                    </div>
                  )}
                </div>
                <p className="text-[#A8B5AE] text-xs mt-1 truncate text-center">{card.name}</p>
                {card.rarity && (
                  <p className="text-[#C9A962] text-[10px] text-center truncate">{card.rarity}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-4 mt-8">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="p-2 rounded-lg bg-[#0F1F1A] border border-[#C9A962]/20 text-[#A8B5AE] hover:text-[#F5F0E6] disabled:opacity-30 disabled:cursor-not-allowed"
            data-testid="prev-page"
          >
            <ChevronLeft size={20} />
          </button>
          <span className="text-[#F5F0E6] text-sm">
            {text(`Página ${page} de ${totalPages}`, `Page ${page} of ${totalPages}`)}
          </span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="p-2 rounded-lg bg-[#0F1F1A] border border-[#C9A962]/20 text-[#A8B5AE] hover:text-[#F5F0E6] disabled:opacity-30 disabled:cursor-not-allowed"
            data-testid="next-page"
          >
            <ChevronRight size={20} />
          </button>
        </div>
      )}
    </div>
  );
}
